import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import type { SearchSubmitOptions } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import {
  createCompletedDocChatTurn,
  createDocChatTurn,
  createErroredDocChatTurn,
  hasDocChatConversationEvents,
  hasInProgressDocChatTurn,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import { DOC_CHAT_STOP_PATH } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatFetcher'
import { stopDocChatStream } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatInFlightStore'
import { applyDocChatStopOutcome } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatStopOutcome'
import type { DocChatSearchResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  DocChatConversation,
  DocChatTurn,
  DocumentSearchMode,
  SearchStateUpdate,
} from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useMemo } from 'react'

export interface UseDocChatHeaderStateParams {
  conversation: DocChatConversation | undefined
  result: DocChatSearchResult | undefined
  isLoadingDocuments: boolean
  query: string
  siteId: string
  tabId?: string
  searchMode: DocumentSearchMode
  onSearchSubmit: (options?: SearchSubmitOptions) => void
  onSearchKeyDown: (event: React.KeyboardEvent) => void
  onSearchStateChange: (updates: SearchStateUpdate) => void
}

export interface UseDocChatHeaderStateResult {
  turns: DocChatTurn[]
  canSubmit: boolean
  isGenerating: boolean
  onDocChatSubmit: () => void
  onDocChatKeyDown: (event: React.KeyboardEvent) => void
  onDocChatStop: () => void
}

export function useDocChatHeaderState({
  conversation,
  result,
  isLoadingDocuments,
  query,
  siteId,
  tabId,
  searchMode,
  onSearchSubmit,
  onSearchKeyDown,
  onSearchStateChange,
}: UseDocChatHeaderStateParams): UseDocChatHeaderStateResult {
  const { authorization } = useGeckoAuth()
  const { POWER_BACKEND_AI_URL } = useRuntimeVariables()
  const shouldSubmitDocChatTurn = searchMode === 'ai'
  const turns = useMemo<DocChatTurn[]>(() => {
    if (
      conversation?.turns.length &&
      conversation.turns.every(hasDocChatConversationEvents)
    ) {
      return conversation.turns
    }
    const trimmedQuery = query.trim()
    if (!trimmedQuery) return []
    if (result && result.query === query) {
      return [
        createCompletedDocChatTurn({
          turnId: 'docchat-current-turn',
          result,
        }),
      ]
    }
    return [
      isLoadingDocuments
        ? createDocChatTurn({
            turnId: 'docchat-current-turn',
            query: trimmedQuery,
          })
        : createErroredDocChatTurn({
            turnId: 'docchat-current-turn',
            query: trimmedQuery,
            siteId,
          }),
    ]
  }, [conversation, result, isLoadingDocuments, query, siteId])

  const canSubmit = shouldSubmitDocChatTurn || !hasInProgressDocChatTurn(turns)
  const isGenerating = Boolean(
    conversation && hasInProgressDocChatTurn(conversation.turns)
  )

  const onDocChatSubmit = useCallback(() => {
    if (!canSubmit) return
    onSearchSubmit({
      clearInputAfterSubmit: shouldSubmitDocChatTurn,
      startDocChatTurn: shouldSubmitDocChatTurn,
    })
  }, [canSubmit, onSearchSubmit, shouldSubmitDocChatTurn])

  const onDocChatStop = useCallback(() => {
    if (!conversation || !authorization) return
    void stopDocChatStream({
      conversation,
      tabId,
      authorization,
      siteId,
      url: `${POWER_BACKEND_AI_URL}${DOC_CHAT_STOP_PATH}`,
    }).then((outcome) => {
      if (!outcome) return
      onSearchStateChange((currentSearchState) => {
        const currentDocChat = currentSearchState.docChat
        if (currentDocChat?.siteId !== siteId) return {}
        const nextConversation = applyDocChatStopOutcome({
          conversation: currentDocChat.conversation,
          outcome,
          siteId,
        })
        if (nextConversation === currentDocChat.conversation) return {}
        return {
          docChat: {
            ...currentDocChat,
            siteId,
            conversation: nextConversation,
          },
        }
      })
    })
  }, [
    conversation,
    onSearchStateChange,
    siteId,
    tabId,
    authorization,
    POWER_BACKEND_AI_URL,
  ])

  const onDocChatKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.stopPropagation()
        if (event.shiftKey || event.nativeEvent.isComposing || !canSubmit)
          return
        onSearchSubmit({
          clearInputAfterSubmit: shouldSubmitDocChatTurn,
          startDocChatTurn: shouldSubmitDocChatTurn,
        })
        return
      }
      onSearchKeyDown(event)
    },
    [canSubmit, onSearchKeyDown, onSearchSubmit, shouldSubmitDocChatTurn]
  )

  return {
    turns,
    canSubmit,
    isGenerating,
    onDocChatSubmit,
    onDocChatKeyDown,
    onDocChatStop,
  }
}
